import { useRef, useState, type FC } from "react";
import { isRecord, PORTFOLIO_VALUATION_STATUS_OBJ } from "@signals/shared";
import { Button, formatCurrency, Loading, Modal } from "../../_common";
import {
  useApplyPortfolioSnapshotRecalculation,
  usePortfolioSnapshot,
  usePortfolioSnapshotRevisions,
  usePreviewPortfolioSnapshotRecalculation,
  useRestorePortfolioSnapshotRevision,
  type PortfolioSnapshotRecalculationPreview,
} from "../portfolio.hooks";

interface SnapshotRevisionModalProps {
  open: boolean;
  portfolioId: string;
  date: string;
  onClose: () => void;
}

function amount(snapshot: Record<string, unknown>, key: string): number | null {
  const value = snapshot[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function quality(snapshot: Record<string, unknown>): string {
  if (snapshot["valuationStatus"] === PORTFOLIO_VALUATION_STATUS_OBJ.INCOMPLETE) return "Incomplete valuation";
  if (snapshot["isEstimated"] === true) return "Preliminary estimate";
  if (snapshot["isEstimated"] === false) return "Complete valuation";
  return "Valuation quality was not recorded for this version.";
}

function coverage(snapshot: Record<string, unknown>): string {
  const fields = snapshot["estimateCoverage"];
  if (!isRecord(fields)) return "Position coverage was not recorded.";
  const count = (key: string) => Array.isArray(fields[key]) ? fields[key].length : 0;
  return `Positions: ${count("fresh")} fresh, ${count("stale")} older prices, ${count("manual")} manual, ${count("excluded")} unavailable.`;
}

function ValuationAmounts({ value }: { value: Record<string, unknown> }) {
  return (
    <>
      <p>Value: {formatCurrency(amount(value, "totalValueUSD"), "USD")}</p>
      <p>Calculated value: {formatCurrency(amount(value, "partialValueUSD"), "USD")}</p>
      <p>Unrealized P&amp;L: {formatCurrency(amount(value, "totalUnrealizedPnLUSD"), "USD")}</p>
      <p>Calculated unrealized P&amp;L: {formatCurrency(amount(value, "partialUnrealizedPnLUSD"), "USD")}</p>
      <p>{quality(value)}</p>
      <p>{coverage(value)}</p>
    </>
  );
}

// Mount a fresh dialog for each scope/opening so a preview cannot carry over
// to another portfolio/date or survive cancellation.
export const SnapshotRevisionModal: FC<SnapshotRevisionModalProps> = (props) =>
  props.open ? <SnapshotRevisionDialog key={`${props.portfolioId}:${props.date}`} {...props} /> : null;

const SnapshotRevisionDialog: FC<SnapshotRevisionModalProps> = ({ portfolioId, date, onClose }) => {
  const snapshot = usePortfolioSnapshot(portfolioId, date);
  const revisions = usePortfolioSnapshotRevisions(portfolioId, date);
  const restore = useRestorePortfolioSnapshotRevision(portfolioId, date);
  const previewRequest = usePreviewPortfolioSnapshotRecalculation(portfolioId, date);
  const apply = useApplyPortfolioSnapshotRecalculation(portfolioId, date);
  const [preview, setPreview] = useState<PortfolioSnapshotRecalculationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const operationInFlight = useRef(false);
  const busy = restore.isPending || previewRequest.isPending || apply.isPending;
  const close = () => { if (!operationInFlight.current) onClose(); };

  const handlePreview = async () => {
    if (operationInFlight.current) return;
    operationInFlight.current = true;
    setPreview(null);
    setError(null);
    try {
      setPreview(await previewRequest.mutateAsync());
    } catch {
      setError("Could not compare the saved valuation. Try previewing again.");
    } finally {
      operationInFlight.current = false;
    }
  };

  const handleApply = async () => {
    if (!preview || operationInFlight.current) return;
    operationInFlight.current = true;
    setError(null);
    try {
      await apply.mutateAsync({ expectedCurrentRevisionId: preview.expectedCurrentRevisionId });
      onClose();
    } catch {
      // Any failed write, including a revision conflict, requires a new preview.
      setPreview(null);
      setError("The update was not confirmed. The saved valuation may have changed. Preview the differences again before saving.");
    } finally {
      operationInFlight.current = false;
    }
  };

  const handleRestore = async (revisionId: string) => {
    if (operationInFlight.current) return;
    if (!window.confirm(`Restore this valuation for ${date}? This creates a new current version and keeps all existing versions.`)) return;
    operationInFlight.current = true;
    setPreview(null);
    setError(null);
    try {
      await restore.mutateAsync({ revisionId, expectedCurrentRevisionId: snapshot.data?.currentRevisionId ?? null });
      onClose();
    } catch {
      setError("Could not restore this valuation. Reload its saved versions and try again.");
      void snapshot.refetch();
      void revisions.refetch();
    } finally {
      operationInFlight.current = false;
    }
  };

  return (
    <Modal open onClose={close} title={`Saved valuation versions · ${date}`}>
      {snapshot.isLoading || revisions.isLoading ? <Loading /> : null}
      {snapshot.error || revisions.error ? <p role="alert" className="text-sm text-destructive">Could not load saved valuation history.</p> : null}
      {!snapshot.isLoading && !revisions.isLoading && revisions.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">There are no saved valuation versions for this date.</p>
      ) : null}
      <ul className="space-y-3">
        {revisions.data?.items.map((revision) => {
          const isCurrent = snapshot.data?.currentRevisionId === revision.id;
          return (
            <li key={revision.id} className="rounded-md border p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">Version {revision.revision}{isCurrent ? " · current" : ""}</p>
                  <p className="mt-1 text-sm">Value: {formatCurrency(amount(revision.snapshotData, "totalValueUSD"), "USD")}; unrealized P&amp;L: {formatCurrency(amount(revision.snapshotData, "totalUnrealizedPnLUSD"), "USD")}</p>
                  <p className="text-xs text-muted-foreground">{quality(revision.snapshotData)}</p>
                </div>
                {!isCurrent && snapshot.data?.currentRevisionId ? (
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => void handleRestore(revision.id)}>
                    Restore this version
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {snapshot.data && !snapshot.error && !revisions.error ? (
        <section className="mt-5 space-y-3 border-t pt-4" aria-label="Update saved valuation">
          <h3 className="font-medium">Review updated calculation</h3>
          <p className="text-sm text-muted-foreground">Compare this saved date using the current calculation rules. Saving creates a new version and keeps the previous one. New FMP and manual prices already update saved dates automatically.</p>
          {!preview ? (
            <Button variant="outline" disabled={busy} onClick={() => void handlePreview()}>
              {previewRequest.isPending ? "Comparing…" : "Preview differences"}
            </Button>
          ) : (
            <>
              <div className="grid gap-4 text-sm sm:grid-cols-2">
                <div aria-label="Saved valuation"><h4 className="font-medium">Saved valuation · USD</h4><ValuationAmounts value={preview.before} /></div>
                <div aria-label="Updated valuation"><h4 className="font-medium">Updated valuation · USD</h4><ValuationAmounts value={preview.after} /></div>
              </div>
              <p className="text-sm">Value difference: {formatCurrency(preview.delta.totalValueUSD, "USD")}; calculated value difference: {formatCurrency(preview.delta.partialValueUSD, "USD")}; unrealized P&amp;L difference: {formatCurrency(preview.delta.totalUnrealizedPnLUSD, "USD")}; calculated P&amp;L difference: {formatCurrency(preview.delta.partialUnrealizedPnLUSD, "USD")}</p>
              <div className="flex flex-wrap gap-2">
                <Button disabled={busy} onClick={() => void handleApply()}>{apply.isPending ? "Saving…" : "Save updated valuation"}</Button>
                <Button variant="outline" disabled={busy} onClick={() => { setPreview(null); setError(null); }}>Cancel update</Button>
              </div>
            </>
          )}
        </section>
      ) : null}
      {error ? <p role="alert" className="mt-3 text-sm text-destructive">{error}</p> : null}
      <div className="mt-4 flex justify-end"><Button variant="outline" disabled={busy} onClick={close}>Close</Button></div>
    </Modal>
  );
};
